import { useEffect, type ReactNode } from 'react'
import { Navigate, Outlet, useLocation, useParams } from 'react-router-dom'
import { useSession } from '../stores/session'

/** Rotas do painel: exigem PERSONAL. */
export function RequirePersonal(): ReactNode {
  const bootstrap = useSession((state) => state.bootstrap)
  const status = useSession((state) => state.status)
  const user = useSession((state) => state.user)
  const location = useLocation()

  useEffect(() => {
    bootstrap(undefined, 'panel')
  }, [bootstrap])

  if (status === 'loading') return <BootSplash />

  if (status === 'anonymous' || !user) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />
  }

  // Um aluno com token válido não entra no painel.
  if (user.role !== 'PERSONAL') {
    return <Navigate to={`/app/${user.personal.slug}`} replace />
  }

  return <Outlet />
}

/**
 * Rotas do portal: exigem STUDENT E que o slug da sessão bata com o da URL.
 * Sem a segunda condição, um aluno de A com sessão válida acessaria a
 * casca do app de B. Ver docs/specs/16-security.md.
 */
export function RequireStudent(): ReactNode {
  const { slug = '' } = useParams<{ slug: string }>()
  const status = useSession((state) => state.status)
  const user = useSession((state) => state.user)

  if (status === 'loading') return <BootSplash />

  if (status === 'anonymous' || !user) {
    return <Navigate to={`/app/${slug}/login`} replace />
  }

  if (user.role !== 'STUDENT' || user.personal.slug !== slug) {
    return <Navigate to={`/app/${slug}/login`} replace />
  }

  return <Outlet />
}

/** Já autenticado não deve ver login nem cadastro. */
export function RedirectIfAuthenticated(): ReactNode {
  const bootstrap = useSession((state) => state.bootstrap)
  const status = useSession((state) => state.status)
  const user = useSession((state) => state.user)

  useEffect(() => {
    bootstrap(undefined, 'panel')
  }, [bootstrap])

  if (status === 'loading') return <BootSplash />

  if (status === 'authenticated' && user?.role === 'PERSONAL') {
    return <Navigate to="/dashboard" replace />
  }

  return <Outlet />
}

/**
 * Rota inexistente DENTRO do app de um personal.
 *
 * Sem isto o `*` global mandava o aluno para /dashboard, ou seja: para a
 * tela de login do PAINEL. Um link velho no WhatsApp jogava o aluno num
 * produto que não é o dele — e ainda por cima com a marca errada.
 */
export function PortalFallback(): ReactNode {
  const { slug = '' } = useParams<{ slug: string }>()
  return <Navigate to={`/app/${slug}`} replace />
}

function BootSplash(): ReactNode {
  return (
    <div className="grid min-h-dvh place-items-center bg-bg">
      <span className="sr-only">Carregando</span>
      <div aria-hidden className="size-10 animate-pulse rounded-xl bg-surface-sunken" />
    </div>
  )
}
